// The plan's tools in the 3D view (docs/claude/rendering.md, *Plan tools in
// 3D*): a click works on what the view shows — where it lands (`land3dAt`,
// viewPicks.ts: the first surface no gesture sees through, else the floor; the
// two-point tools snap to the drawing's snap points there) —, its plan point
// handed to the tool's own plan click, so a tool does there what it does on the
// plan. One row
// per tool whose place works in 3D (`ToolIn3d`, main.ts's `TOOL_PLACE`): a new
// one does not compile until it has its row.
import {
  instanceToWorld,
  toInstanceLocal,
} from '../geometry';
import {
  type Pt3,
  rayToLevel,
} from '../handles';
import { tr } from '../i18n';
import { drawingDimStyle } from '../dimFormat';
import { measureReadout } from '../measure3d';
import { type Pick3d } from '../three/viewer3d';
import { drawSnapMarker } from '../render';
import { PLAN_PLANE } from '../workPlane';
import {
  type Landing,
  type LandRule,
  liftSnapPoints,
  snapAt,
  type SnapPoint3,
} from '../viewPicks';
import { snapCandidates } from './snapSet';
import { type SnapCandidate } from '../anchors';
import {
  benchAreaAt,
  type BenchHover,
  doorAreaAt,
  downBenchTool,
  downDoorTool,
  downGlassTool,
  downHeaterWallTool,
  downInnerWallTool,
  downOpeningTool,
  dragLive3d,
  dragMove3d,
  dragRun3d,
  endDrag,
  endOneShot,
  flashToolStatus,
  innerWallAreaAt,
  mirrorClickAt,
  mirrorFromNow,
  pickBase3dNow,
  pickClick3d,
  pickMoving3d,
  previewPick3d,
  productOf,
  screenPos,
  sectionClickAt,
  sectionDraftNow,
  sectionPreview,
  type ToolIn3d,
  type ToolOnScene3d,
} from '../main';
import {
  instLocked,
  invalidate,
  notePress,
  state,
  type Tool,
} from './core';
import {
  coveHoverAt,
  downCoveTool,
  downStripTool,
  kitStep,
  skyAreaAt,
  skyClick3d,
  spotAreaAt,
  spotClick3d,
  startSpotDrag,
  stripHoverAt,
} from './lightTools';
import {
  measure3dClick,
  measure3dHover,
} from './measure3dTool';
import {
  backrestHoverAt,
  benchEndHoverAt,
  downBackrestTool,
  downBenchEndTool,
  selectedFixtureNow,
} from './roomFixtures';
import {
  downBenchSupportTool,
  supportAreaAt,
} from './benchSupportTool';
import {
  downFloorGrilleTool,
  floorGrilleHoverAt,
} from './floorGrilleUi';
import { heaterWallHoverAt } from './heaterWalls';
import {
  benchRoundHoverAt,
  downBenchRoundTool,
} from './benchRoundTool';
import {
  downSculptBenchTool,
  sculptBenchHoverAt,
} from './sculptBenchUi';
import { cancelHeldUnderWay } from './underWayUi';
import { type ProductInstance } from '../../shared/types';
import { type SpotLight } from '../lights';
import {
  landedAim,
  type ToolAim,
} from '../toolAim';
import {
  land3dAt,
  placeSection3d,
  previewSection3d,
  rebuildObjects3d,
  refreshView3d,
  toPlan,
  view3d,
  viewer3d,
} from './view3d';

type Vec2 = { x: number; y: number };

/** Where a click in the 3D view lands: the point (mm), what it is on (null on
 *  the floor) and the kind of snap point it snapped to (the OSNAP marker's
 *  name), if it did. */
export interface Land { point: Pt3; pick: Pick3d | null; snap: string | null }

/** Where the overlay draws a point of the drawing (css px), or null off screen. */
type OnScreen = (p: Vec2, z?: number) => Vec2 | null;

/** A tool's life in the 3D view: everything the 3D pointer (pointer3d.ts) and
 *  the overlay do with it reads its row, never its name. */
export interface Tool3dRow {
  /** A left click, where it lands. */
  click: (e: PointerEvent, land: Land) => void;
  /** What the overlay previews: the area a click adds or takes (the plan's
   *  green / red: `area`), a line from the first point (`line`), the window's
   *  rectangle — or nothing of this module's (Measure paints its own). */
  preview: 'area' | 'line' | 'none' | 'rect';
  /** A room tool's area (`preview: 'area'`): what a click at `aim` would add
   *  or take, the one the plan's hover shows too (main.ts `planToolAreaAt`). */
  area?: (e: MouseEvent, aim: ToolAim) => BenchHover | null;
  /** Its points snap to the drawing's snap points near the pointer (Alt frees
   *  them), as the plan's OSNAP does (`snap3dAt`). */
  snaps?: true;
  /** A press is its plan press at the plan point it lands on, which may start
   *  a drag: the drag then follows the line it runs along (`dragRun3d`), else
   *  the level pressed. A click is the drag's own. */
  press?: (e: PointerEvent, aim: ToolAim) => void;
  /** Its press lands on the FIRST thing under the pointer, see-through or not
   *  (a pane, an X-rayed wall, an opening's hole: the Glass and Opening tools
   *  work on walls), not on the first solid one. */
  firstHit?: true;
  /** A press on a spot drags it: any spot (the Spot tool; in Select only the
   *  one selected, its `SCENE_3D` row). */
  spots?: 'any';
  /** A click sees through what follows the pointer (Move's selection). */
  carries?: true;
  /** The selection's box shows, in the scene, where it would go from the base
   *  point (Copy's ghost: its selection stays put). */
  ghostBox?: true;
  /** What else follows the pointer, from where a click would land there (null:
   *  nowhere, or the pointer is another's) — Measure's line from its first point. */
  hover?: (land: Land | null) => void;
  /** Its line (`preview: 'line'`) from its first point to where a click lands
   *  (`at`), and the seam it leaves ('' for none drawn). */
  line?: (ctx: CanvasRenderingContext2D, on: OnScreen, at: Pt3 | null) => string;
  /** The pointer's cursor while it is armed (the 3D section's crosshair). */
  cursor?: string;
  /** It reads the view itself, not where a click lands (the 3D section: the
   *  surface under the pointer and its normal): its click, and its preview at a
   *  client point (null: none), which replaces the hover outline. */
  own?: { click: (e: PointerEvent) => void; hover: (at: Vec2 | null) => void };
  /** While it is armed the 3D view shows the drawing's 3D section as its work:
   *  the plane it lies on, the one the pointer previews, and its handle
   *  (whatever is selected). */
  section?: true;
}

/** A tool whose clicks in 3D are the scene's (main.ts `ToolOnScene3d`: Select,
 *  Transform, the arrays): what a press on the scene does with it. */
export interface Scene3dRow {
  /** A press on a spot drags it: the one selected (Select). */
  spots?: 'selected';
}

const planOf = (p: Pt3): Vec2 => ({ x: Math.round(p.x), y: Math.round(p.y) });
const round3 = (p: Pt3): Pt3 => ({ x: Math.round(p.x), y: Math.round(p.y), z: Math.round(p.z) });
/** How near the pointer a snap point snaps (css px). */
const SNAP_PX = 12;
/** How far behind where the pointer lands a snap point may lie (mm, at the
 *  least: a face's own corners recede from the point hit). */
const SNAP_DEPTH_MM = 20;

/** The 3D view's aim (toolAim.ts) where a click lands: its plan point in whole
 *  mm, the view's own px per mm there (not the plan's zoom), what it is on. */
export function aim3dOf(e: MouseEvent, land: Land): ToolAim {
  return landedAim({ ...planOf(land.point), z: land.point.z }, viewer3d?.pxPerMmAt(land.point) ?? 0, land.pick, e.altKey);
}

type AreaAt = (e: MouseEvent, aim: ToolAim) => BenchHover | null;
/** A room tool: one click at its aim, as on the plan, and the area that click
 *  would add or take. */
const room = (click: (e: MouseEvent, aim: ToolAim) => unknown, area: AreaAt): Tool3dRow => ({ click: (e, land) => { click(e, aim3dOf(e, land)); }, preview: 'area', area });
/** A tool whose press drags along a wall or a bench (as on the plan), and the
 *  area its click would take (null: its drag shows what it adds, not a hover). */
const drags = (press: (e: PointerEvent, aim: ToolAim) => unknown, area: AreaAt | null, onWalls = false): Tool3dRow => ({ click: () => {}, preview: 'area', ...(area ? { area } : {}), press: (e, a) => { press(e, a); }, ...(onWalls ? { firstHit: true as const } : {}) });

/** Move / Copy: the base point, then where it goes, its height too (Move's
 *  selection follows the pointer, a click seeing through it; Copy's ghost box). */
const picks = (copy: boolean): Tool3dRow => ({
  click: (_e, land) => { pickClick3d(round3(land.point)); },
  preview: 'line',
  snaps: true,
  line: pickLine,
  ...(copy ? { ghostBox: true as const } : { carries: true as const }),
});

export const TOOL_3D: Record<ToolIn3d, Tool3dRow> = {
  backrest: room(downBackrestTool, (_e, a) => backrestHoverAt(a)),
  'sculpt-bench': room(downSculptBenchTool, (_e, a) => sculptBenchHoverAt(a)),
  bench: room((_e, a) => downBenchTool(a), (_e, a) => benchAreaAt(a)),
  'bench-end': room(downBenchEndTool, (_e, a) => benchEndHoverAt(a)),
  'bench-round': room(downBenchRoundTool, (_e, a) => benchRoundHoverAt(a)),
  'bench-support': drags((e, a) => downBenchSupportTool(e, screenPos(e), a), supportAreaAt),
  copy: picks(true),
  door: room((_e, a) => downDoorTool(a), (_e, a) => doorAreaAt(a)),
  'floor-grille': room(downFloorGrilleTool, (_e, a) => floorGrilleHoverAt(a)),
  glass: drags((_e, a) => downGlassTool(a), null, true),
  'heater-wall': room(downHeaterWallTool, (_e, a) => heaterWallHoverAt(a)),
  'inner-wall': drags((_e, a) => downInnerWallTool(a), (_e, a) => innerWallAreaAt(a)),
  'light-cove': room(downCoveTool, (_e, a) => coveHoverAt(a)),
  'light-sky': room((e, a) => {
    if (skyClick3d(e, a)) return;
    toPlan();
    flashToolStatus(tr('Draw the zone on the plan.'));
  }, skyAreaAt),
  'light-spot': { ...room(spotClick3d, spotAreaAt), spots: 'any' },
  'light-strip': room(downStripTool, (_e, a) => stripHoverAt(a)),
  'light-strip-up': room(downStripTool, (_e, a) => stripHoverAt(a)),
  measure: { click: (_e, land) => { measure3dClick(land.point); }, preview: 'none', snaps: true, hover: (land) => { measure3dHover(land?.point ?? null); } },
  // The mirror is the plumb plane through the line clicked.
  mirror: { click: (_e, land) => { mirrorClickAt(planOf(land.point), PLAN_PLANE); }, preview: 'line', snaps: true, line: mirrorLine },
  move: picks(false),
  opening: drags((_e, a) => downOpeningTool(a), null, true),
  section: { click: (_e, land) => { sectionClickAt(planOf(land.point)); }, preview: 'line', snaps: true, line: sectionLine },
  // The 3D section lays its plane where it previews it: on the surface under
  // the pointer, or through the point there (view3d.ts).
  'section-3d': {
    click: () => {},
    preview: 'none',
    cursor: 'crosshair',
    section: true,
    own: {
      click: (e) => { previewSection3d({ x: e.clientX, y: e.clientY }); placeSection3d(); },
      hover: previewSection3d,
    },
  },
  zoom: { click: (e) => { zoomClick3d(e); }, preview: 'rect' },
};

/** Each scene tool's row: a Record, so a tool whose 3D clicks become the
 *  scene's says what its press there does. */
export const SCENE_3D: Record<ToolOnScene3d, Scene3dRow> = {
  'array-polar': {},
  'array-rect': {},
  select: { spots: 'selected' },
  transform: {},
};

const rowOf = (t: Tool): Tool3dRow | undefined => (TOOL_3D as Partial<Record<Tool, Tool3dRow>>)[t];
/** Tool `t`'s row, or none (the plan's hover reads its `area`). */
export const tool3dRowOf = rowOf;
/** The armed tool's row, or none: a tool without one does not work in the 3D view. */
export const tool3dRow = (): Tool3dRow | undefined => rowOf(state.tool);
/** The armed tool's scene row (`SCENE_3D`), or none. */
const scene3dRow = (): Scene3dRow | undefined => (SCENE_3D as Partial<Record<Tool, Scene3dRow>>)[state.tool];

/** Where a click at the event's point lands for `row` (`land3dAt` by `rule`:
 *  what X-ray, glass and a live Move's selection let a click through seen
 *  through), else the floor; for a tool that snaps, the drawing's snap point
 *  nearest the pointer there ({@link snap3dAt}), unless Alt is down. */
function landAt(e: MouseEvent, row: Tool3dRow, rule: LandRule = 'solid'): Land | null {
  const land = land3dAt(e.clientX, e.clientY, rule);
  if (!land) return null;
  const s = row.snaps && !e.altKey ? snap3dAt(e, land) : null;
  return s ? { point: s.p, pick: land.src, snap: s.kind ?? 'endpoint' } : { point: land.point, pick: land.src, snap: null };
}

/** What a 3D landing snaps to, as the move gizmo does: the drawing's snap set
 *  (`snapCandidates`, the plan's — what a live Move carries and what the 3D
 *  view keeps out left out), each point at the floor and at its own object's
 *  heights (`snapPoints3d`); the one nearest the pointer within `SNAP_PX` that
 *  the view shows there (viewPicks.ts `snapAt`: kept by the section, not behind
 *  the landing). */
function snap3dAt(e: MouseEvent, land: Landing): { p: Pt3; kind?: string } | null {
  const v = viewer3d;
  if (!v) return null;
  const except = new Set([...(moving3d() ?? []), ...view3d.view3dTempHidden]);
  const r = (document.getElementById('view3d-canvas') as HTMLCanvasElement).getBoundingClientRect();
  const k = v.pxPerMmAt(land.point);
  return snapAt({ x: e.clientX - r.left, y: e.clientY - r.top }, land, snapPoints3d(snapCandidates(except)),
    { project: (p) => v.project(p), eye: v.eye(), keeps: (p) => v.keeps(p) }, SNAP_PX, Math.max(SNAP_DEPTH_MM, k > 0 ? SNAP_PX / k : 0));
}

/** A snap set's points in 3D (viewPicks.ts `liftSnapPoints`: each at its own
 *  object's heights), lifted once per set: the plan's set is rebuilt whenever the
 *  drawing changes, and only then. */
const lifted = new WeakMap<readonly SnapCandidate[], SnapPoint3[]>();
export function snapPoints3d(plan: readonly SnapCandidate[]): SnapPoint3[] {
  let pts = lifted.get(plan);
  if (!pts) lifted.set(plan, pts = liftSnapPoints(plan, { drawing: state.drawing, template: productOf }));
  return pts;
}

/** What a live Move carries along with the pointer (a click sees through it),
 *  else null. Copy leaves the selection where it stands — a copy can go onto
 *  it — and a ghost of its box follows the pointer instead. */
const moving3d = (): Set<string> | null => (rowOf(state.tool)?.carries ? pickMoving3d() : null);

/** Where the pointer is over the view (client px, where a click there lands),
 *  and the area a room tool's click there would add or take, at that height. */
let pointer: { x: number; y: number; land: Land | null } | null = null;
let area: { tool: Tool; hover: BenchHover; z: number; key: string } | null = null;
/** The Zoom window's first corner (client px). */
let zoomFrom: Vec2 | null = null;

/** A left click in the 3D view with a tool of `TOOL_3D` armed: its row's. True
 *  when the tool took it. */
export function click3dTool(e: PointerEvent, row = rowOf(state.tool)): boolean {
  if (!row || !viewer3d) return false;
  if (row.own) row.own.click(e);
  else {
    const land = landAt(e, row);
    if (land) row.click(e, land);
  }
  hover3dTool(e); // what a click there does now
  return true;
}

/** The pointer moving over the 3D view: the armed tool's preview follows it —
 *  Move / Copy's selection itself, as on the plan —, or (`on` false: the pointer
 *  is another's, pointer3d.ts) shows nothing. */
export function hover3dTool(e: PointerEvent, on = true): void {
  const tool = rowOf(state.tool), row = on ? tool : undefined, v = viewer3d;
  if (tool?.own) {
    tool.own.hover(row && !e.buttons ? { x: e.clientX, y: e.clientY } : null);
    pointer = null;
    area = null;
    return;
  }
  const land = row && v ? landAt(e, row) : null;
  tool?.hover?.(land);
  pointer = row ? { x: e.clientX, y: e.clientY, land } : null;
  const hover = row?.area && land && !e.buttons ? row.area(e, aim3dOf(e, land)) : null;
  area = hover && land ? { tool: state.tool, hover, z: land.point.z, key: JSON.stringify([hover.instId, hover.kind, hover.polys, hover.shift, Math.round(land.point.z)]) } : null;
  // Move / Copy's typed distance goes toward the pointer, as on the plan; Move's
  // selection goes there with it.
  if (land && pickBase3dNow()) state.hoverWorld = planOf(land.point);
  const moving = moving3d();
  if (land && moving) {
    previewPick3d(land.point);
    rebuildObjects3d([...moving]);
  }
  syncGhosts();
  if (tool && tool.preview !== 'none') v?.redraw();
}

/** The pointer left the view: no preview — unless a drag here has it (its
 *  capture moves the pointer off the canvas). */
export function leave3dTool(): void {
  rowOf(state.tool)?.own?.hover(null);
  if (drag3d || (!pointer && !area)) return;
  pointer = null;
  area = null;
  syncGhosts();
  viewer3d?.redraw();
}

// ---- What a tool would add or take, in the scene ----------------------------------

/** A ghost slot's key as shown (`setGhost` is called only when it changes). */
const ghostShown: Record<'aim' | 'copy', string> = { aim: '', copy: '' };

/** Show `slot` as `key` says — built by `parts` —, unless it already shows so. */
function showGhost(slot: 'aim' | 'copy', key: string, parts: () => GhostPart[]): void {
  if (ghostShown[slot] === key) return;
  ghostShown[slot] = key;
  viewer3d?.setGhost(slot, key ? parts() : null);
}

type GhostPart = { ring: Vec2[]; z0: number; z1: number; color: string; opacity: number };

/** The colour a click's preview shows in: what it adds, takes, picks. */
const AIM_COLOUR: Record<BenchHover['kind'], string> = { add: '#22c55e', delete: '#ef4444', pick: '#3b82f6' };
/** Half the thickness of a click's preview, about the height pointed at (mm). */
const AIM_HALF = 12;

/** A click's preview as the scene shows it: the plan's area (room local, its
 *  place's shift added) as a thin slab at the height pointed at. */
function aimParts(a: { hover: BenchHover; z: number }): GhostPart[] {
  const inst = state.drawing.instances.find((i) => i.id === a.hover.instId);
  if (!inst) return [];
  const shift = a.hover.shift ?? { x: 0, y: 0 };
  return a.hover.polys.filter((p) => p.length >= 3).map((poly) => ({
    ring: poly.map((q) => { const w = instanceToWorld(inst, q); return { x: w.x + shift.x, y: w.y + shift.y }; }),
    z0: a.z - AIM_HALF, z1: a.z + AIM_HALF, color: AIM_COLOUR[a.hover.kind], opacity: 0.45,
  }));
}

/** Keep the scene's previews to what the pointer shows now: the armed room
 *  tool's click (its area, `aim`) and Copy's box where the copy would go
 *  (`copy`). A tool's drag has its own (`drag`, `showDragGhost`). */
function syncGhosts(): void {
  const row = rowOf(state.tool), live = !!pointer && state.viewTab === '3d';
  const aim = live && !drag3d && row?.preview === 'area' && area && area.tool === state.tool ? area : null;
  showGhost('aim', aim?.key ?? '', () => aimParts(aim!));
  const base = row?.ghostBox ? pickBase3dNow() : null, at = live ? pointer?.land?.point : null;
  const box = base && at ? viewer3d?.selectionBounds() : null;
  const d = base && at ? { x: at.x - base.x, y: at.y - base.y, z: at.z - base.z } : null;
  showGhost('copy', box && d ? JSON.stringify([box, Math.round(d.x), Math.round(d.y), Math.round(d.z)]) : '', () => [{
    ring: [{ x: box!.min.x + d!.x, y: box!.min.y + d!.y }, { x: box!.max.x + d!.x, y: box!.min.y + d!.y }, { x: box!.max.x + d!.x, y: box!.max.y + d!.y }, { x: box!.min.x + d!.x, y: box!.max.y + d!.y }],
    z0: box!.min.z + d!.z, z1: box!.max.z + d!.z, color: '#22c55e', opacity: 0.3,
  }]);
}

/** Whether the Zoom window has its first corner in the 3D view. */
export const zoom3dStarted = (): boolean => zoomFrom != null;
/** Let the Zoom window's first corner go. */
export function resetZoom3d(): void {
  if (!zoomFrom) return;
  zoomFrom = null;
  viewer3d?.redraw();
}

/** A Zoom window click: its first corner, then the opposite one — the camera
 *  glides to what the window shows. */
function zoomClick3d(e: PointerEvent): void {
  const p = { x: e.clientX, y: e.clientY };
  if (!zoomFrom) { zoomFrom = p; viewer3d?.redraw(); return; }
  const a = zoomFrom;
  zoomFrom = null;
  if (viewer3d?.zoomToRect(a, p, land3dAt((a.x + p.x) / 2, (a.y + p.y) / 2)?.point ?? null)) endOneShot();
}

/** Paint the armed tool's marks on the 3D overlay (`cv`, css px) — what a click
 *  or a drag would add or take shows in the scene itself (`syncGhosts`,
 *  `showDragGhost`): a cross where a room tool's click lands; Move / Copy's line
 *  from its base point with what it moves by; Mirror's
 *  and Section's line on the floor (Section's side, once its cut is drawn, as a
 *  dashed line where the view is drawn from); the Zoom window. Seams:
 *  `data-tool3d` (`add` / `delete` / `pick` / `line` / `rect` / `drag`),
 *  `data-tool3d-at` (where a click lands, `x,y,z` mm) and `data-tool3d-snap`
 *  (`endpoint` when it snapped). */
export function paintTools3d(ctx: CanvasRenderingContext2D, cv: HTMLCanvasElement): void {
  const row = rowOf(state.tool), v = viewer3d;
  let seam = '';
  syncGhosts(); // another tool, another view: their previews go
  if (row && v && pointer && state.viewTab === '3d') {
    const r = cv.getBoundingClientRect();
    const local = (p: Vec2): Vec2 => ({ x: p.x - r.left, y: p.y - r.top });
    const at = pointer.land?.point ?? null;
    const on = (p: Vec2, z = 0): Vec2 | null => v.project({ x: p.x, y: p.y, z });
    ctx.save();
    ctx.lineWidth = 2;
    // The area a click would add or take shows in the scene (`syncGhosts`).
    if (drag3d) seam = 'drag';
    else if (row.preview === 'area' && area && area.tool === state.tool && state.drawing.instances.some((i) => i.id === area!.hover.instId)) seam = area.hover.kind;
    if (row.preview === 'area' && at) {
      const c = on(at, at.z);
      if (c) {
        ctx.strokeStyle = '#ffd166';
        ctx.beginPath();
        ctx.moveTo(c.x - 6, c.y); ctx.lineTo(c.x + 6, c.y);
        ctx.moveTo(c.x, c.y - 6); ctx.lineTo(c.x, c.y + 6);
        ctx.stroke();
      }
    }
    if (row.preview === 'line') seam = row.line?.(ctx, on, at) ?? '';
    // A snapped point: the plan's OSNAP marker, with its name.
    if (pointer.land?.snap && at) {
      const c = on(at, at.z);
      if (c) { ctx.setLineDash([]); drawSnapMarker(ctx, c, pointer.land.snap, cv.clientWidth, cv.clientHeight); }
    }
    if (row.preview === 'rect' && zoomFrom) {
      const a = local(zoomFrom), b = local(pointer);
      ctx.strokeStyle = '#ffd166';
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(a.x - b.x), Math.abs(a.y - b.y));
      seam = 'rect';
    }
    ctx.restore();
  }
  cv.dataset.tool3d = seam;
  const at = row && pointer?.land?.point;
  cv.dataset.tool3dAt = at ? `${Math.round(at.x)},${Math.round(at.y)},${Math.round(at.z)}` : '';
  cv.dataset.tool3dSnap = (row && pointer?.land?.snap) || '';
  cv.dataset.tool3dGhost = ghostShown.copy ? '1' : '';
}

/** A segment on the overlay, in the two-point tools' yellow, dashed or not. */
function seg(ctx: CanvasRenderingContext2D, a: Vec2 | null, b: Vec2 | null, dashed: boolean): void {
  if (!a || !b) return;
  ctx.strokeStyle = '#ffd166';
  ctx.setLineDash(dashed ? [6, 4] : []);
  ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
}

/** Move / Copy's line, from the base point to the pointer, with what it moves
 *  by beside it (Copy's ghost box is in the scene, `syncGhosts`). */
function pickLine(ctx: CanvasRenderingContext2D, on: OnScreen, at: Pt3 | null): string {
  const base = pickBase3dNow();
  if (!base || !at) return '';
  const a = on(base, base.z), b = on(at, at.z);
  seg(ctx, a, b, true);
  if (a && b) {
    const lines = measureReadout(base, at, drawingDimStyle(state.drawing)).lines;
    ctx.font = '12px sans-serif';
    const w = Math.max(...lines.map((l) => ctx.measureText(l).width)) + 12, h = 36;
    const x = (a.x + b.x) / 2 + 10, y = (a.y + b.y) / 2 - h - 6;
    ctx.fillStyle = 'rgba(20, 22, 28, 0.85)';
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = '#e6eefc';
    ctx.textBaseline = 'top';
    lines.forEach((l, i) => ctx.fillText(l, x + 6, y + 4 + i * 15));
  }
  return 'line';
}

/** Mirror's line on the floor, from its first point to the pointer. */
function mirrorLine(ctx: CanvasRenderingContext2D, on: OnScreen, at: Pt3 | null): string {
  const from = mirrorFromNow();
  if (!from || !at) return '';
  seg(ctx, on(from), on(at), true);
  return 'line';
}

/** Section's cut on the floor, and once it is drawn its side: a dashed line
 *  where the view is drawn from. */
function sectionLine(ctx: CanvasRenderingContext2D, on: OnScreen, at: Pt3 | null): string {
  const d = sectionDraftNow();
  if (!d.a || (!d.b && !at)) return '';
  seg(ctx, on(d.a), on(d.b ?? at!), !d.b);
  const prev = d.b && at ? sectionPreview(planOf(at)) : null;
  if (prev && prev.offset > 0) {
    const dx = prev.b.x - prev.a.x, dy = prev.b.y - prev.a.y, l = Math.hypot(dx, dy) || 1;
    const n = { x: (-dy / l) * prev.side * prev.offset, y: (dx / l) * prev.side * prev.offset };
    seg(ctx, on({ x: prev.a.x + n.x, y: prev.a.y + n.y }), on({ x: prev.b.x + n.x, y: prev.b.y + n.y }), true);
  }
  return 'line';
}

// ---- A drag a 3D press started ---------------------------------------------------

/** A drag a press in the 3D view started — a spot's (`spot-move`), the Glass,
 *  Opening, Inner-wall or Bench support tool's —, on the plan's drag life
 *  (main.ts `DRAG_LIFE`: its move, its release, its cancel, what it rebuilds
 *  live): the level it was pressed at, for a drag that follows no line, and how
 *  far the plumb plane it follows stands off that line. This module has the
 *  pointer until it is let go; Esc, a tab switch or closing the view put the drag
 *  back through `cancelDrag`, after which the pointer moves nothing. */
let drag3d: { z: number; shift: Vec2; aim: ToolAim } | null = null;

/** The spot a press in 3D takes, by the armed tool's row (`row`, else its
 *  scene row): any for one whose `spots` is 'any' (the Spot tool — unless a
 *  fibre kit waits for its projector: the click places it, as on the plan), the
 *  one selected for 'selected' (Select: a press elsewhere still turns the view). */
function spotAt3d(e: PointerEvent, row: Tool3dRow | null): { room: ProductInstance; light: SpotLight; z: number } | null {
  const spots = row ? row.spots : scene3dRow()?.spots;
  if (!spots || (spots === 'any' && kitStep)) return null;
  const hit = land3dAt(e.clientX, e.clientY);
  const part = hit?.src?.part;
  if (!hit || part?.kind !== 'fixture') return null;
  const room = state.drawing.instances.find((i) => i.id === hit.src!.id);
  const light = room?.lights?.find((l): l is SpotLight => l.id === part.id && l.kind === 'spot');
  if (!room || !light || instLocked(room)) return null;
  if (spots === 'selected' && selectedFixtureNow()?.id !== light.id) return null;
  return { room, light, z: hit.point.z };
}

/** A left press the 3D pointer gives the armed tool (`row`) or, with none, the
 *  scene (pointer3d.ts `own`): the tool's plan press where it lands, which may
 *  start a drag (`pressTool3d`); else a spot (`spotAt3d`): the plan's own spot
 *  drag (lightTools.ts `startSpotDrag`), across the level it hangs at, its grab
 *  kept — let go where pressed, the Spot tool's press takes it out. True when
 *  it took the press: the caller then gives this module the pointer. */
export function pressOwn3d(e: PointerEvent, row: Tool3dRow | null): boolean {
  if (drag3d) return false;
  if (row && pressTool3d(e, row)) return true;
  const t = spotAt3d(e, row);
  if (!t) return false;
  // Where on it the press took it (room local), so it keeps its place under the pointer.
  const r = viewer3d!.rayAt(e.clientX, e.clientY), p = rayToLevel(r.o, r.d, t.z);
  notePress(screenPos(e));
  startSpotDrag(t.room, t.light, p ? toInstanceLocal(t.room, p) : { ...t.light.at }, { x: 0, y: 0 }, screenPos(e), row?.spots === 'any');
  const at = p ? { ...p, z: t.z } : { ...instanceToWorld(t.room, t.light.at), z: t.z };
  drag3d = { z: t.z, shift: { x: 0, y: 0 }, aim: landedAim(at, viewer3d!.pxPerMmAt(at), null, e.altKey) };
  return true;
}

/** The drags this module's presses start, on the 3D view's overlay: they have
 *  the pointer until it is let go — its moves (`moveDrag3d`), its release (the
 *  plan's `endDrag`, then the scene brought up to the drawing) and a lost
 *  pointer (the gesture under way's `cancelHeldUnderWay`). */
export function installTools3d(overlay: HTMLElement): void {
  overlay.addEventListener('pointermove', (e) => { if (drag3d) moveDrag3d(e); });
  overlay.addEventListener('pointerup', (e) => {
    if (!drag3d) return;
    const aim = drag3d.aim;
    drag3d = null;
    viewer3d?.setGhost('drag', null);
    if (state.dragMode !== 'none') endDrag(e, aim);
    refreshView3d(false);
    hover3dTool(e);
  });
  overlay.addEventListener('pointercancel', () => {
    if (!drag3d) return;
    drag3d = null;
    viewer3d?.setGhost('drag', null);
    cancelHeldUnderWay();
    invalidate();
  });
}

// ---- A room tool's drag in 3D ------------------------------------------------------

/** A press with a tool whose row presses (`press`): its plan press where it
 *  lands. True when that started a drag — this module then has the pointer. */
function pressTool3d(e: PointerEvent, row: Tool3dRow): boolean {
  const v = viewer3d;
  if (!row.press || !v) return false;
  // What it presses is where it lands: one query, its point and what it is on.
  const land = landAt(e, row, row.firstHit ? 'walls' : 'solid');
  if (!land) return false;
  const at = land.point, aim = aim3dOf(e, land);
  notePress(screenPos(e));
  row.press(e, aim);
  if (state.dragMode === 'none') return false; // nothing there to drag: the view takes the press
  // The plumb plane runs through the point pressed, along the line the drag
  // follows: a wall pressed on its outer face measures there, as pressed.
  const run = dragRun3d();
  let shift = { x: 0, y: 0 };
  if (run) {
    const [a, b] = run.line, l = Math.hypot(b.x - a.x, b.y - a.y) || 1, n = { x: (a.y - b.y) / l, y: (b.x - a.x) / l };
    const d = (at.x - a.x) * n.x + (at.y - a.y) * n.y;
    shift = { x: n.x * d, y: n.y * d };
  }
  drag3d = { z: at.z, shift, aim };
  showDragGhost();
  return true;
}

/** Where a ray meets the plumb plane through the plan line `a`–`b`. */
function rayToPlumb(o: Pt3, d: Pt3, [a, b]: [Vec2, Vec2]): Pt3 | null {
  const n = { x: a.y - b.y, y: b.x - a.x }, nd = n.x * d.x + n.y * d.y;
  if (Math.abs(nd) < 1e-9) return null;
  const t = (n.x * (a.x - o.x) + n.y * (a.y - o.y)) / nd;
  return t > 0 ? { x: o.x + d.x * t, y: o.y + d.y * t, z: o.z + d.z * t } : null;
}

/** The pointer moving in a 3D drag: where it meets the line the drag runs
 *  along (the plumb plane through it), else the level pressed, is its aim,
 *  handed to its row's move (its pixel tolerances — a spot's hold on another's
 *  projection — read the view's own px per mm at that point), and kept for
 *  its release. What its row says it changes is rebuilt as it goes
 *  (`live3d`: a spot, a partition or a support slid). */
function moveDrag3d(e: PointerEvent): void {
  const v = viewer3d, d = drag3d;
  if (!v || !d) return;
  // Put back meanwhile (Esc): the pointer moves nothing more.
  if (state.dragMode === 'none') { drag3d = null; v.setGhost('drag', null); return; }
  const r = v.rayAt(e.clientX, e.clientY), run = dragRun3d();
  const line: [Vec2, Vec2] | null = run ? [{ x: run.line[0].x + d.shift.x, y: run.line[0].y + d.shift.y }, { x: run.line[1].x + d.shift.x, y: run.line[1].y + d.shift.y }] : null;
  const level = line ? null : rayToLevel(r.o, r.d, d.z);
  const p = line ? rayToPlumb(r.o, r.d, line) : level && { ...level, z: d.z };
  if (!p) return;
  d.aim = landedAim(p, v.pxPerMmAt(p), null, e.altKey);
  dragMove3d(e, d.aim);
  rebuildObjects3d(dragLive3d());
  showDragGhost();
  v.redraw();
}

/** The colour of what a drag adds, in the scene: glass, else the green of an add. */
const GHOST: Record<'glass' | 'opening' | 'partition', { color: string; opacity: number }> = {
  glass: { color: '#38bdf8', opacity: 0.6 },
  opening: { color: '#22c55e', opacity: 0.4 },
  partition: { color: '#22c55e', opacity: 0.4 },
};

/** Show in the scene what the drag would add now, walls in front of it hiding
 *  it (`setGhost`, slot `drag`): the pane, the opening or the partition where it
 *  goes, else the support row's place, a thin slab at the level pressed. */
function showDragGhost(): void {
  const run = drag3d ? dragRun3d() : null;
  if (run) { viewer3d?.setGhost('drag', run.slab.length ? [{ ring: run.slab, z0: run.z0, z1: run.z1, ...GHOST[run.kind] }] : null); return; }
  const row = drag3d && state.benchAddHover;
  viewer3d?.setGhost('drag', row ? aimParts({ hover: row, z: drag3d!.z }) : null);
}
